import { useState, useEffect, useRef } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toWavChunks } from "@/lib/audioChunk";
import MedicineAutocomplete from "@/components/MedicineAutocomplete";
import OpdPreview from "@/components/OpdPreview";
import ShareMenu from "@/components/ShareMenu";
import { toast } from "sonner";
import {
  Mic, Square, Loader2, Sparkles, UserPlus, Search, Plus, X, ShieldAlert,
  FileDown, FileText, Printer, Save, Upload, Trash2, Stethoscope, IndianRupee,
  AlertTriangle, Activity, Share2, Check, Lock, Pencil,
} from "lucide-react";

const GENDERS = ["Male", "Female", "Other"];
const SAMPLE = "Doctor: Aaiye baithiye. Kya taqleef hai? Patient: Doctor sahab, teen din se tez bukhar hai, sar dard aur badan dard bhi ho raha hai. Khaansi bhi aa rahi hai thodi thodi. Doctor: Gale mein dard? Patient: Haan halka sa. Bhookh bhi nahi lag rahi. Doctor: Koi dawai se allergy? Patient: Penicillin se allergy hai mujhe. Doctor: Theek hai, main aapko jaanch aur dawai likhta hoon.";

export default function LiveConsultationAI({ presetPatientId, clearPreset }) {
  const { user } = useAuth();
  const [step, setStep] = useState("intake"); // intake -> record -> analyze -> review
  const [patient, setPatient] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [form, setForm] = useState({ name: "", age: "", gender: "Male", phone: "", whatsapp: "", email: "", allergies: "", drug_history: "", medical_history: "", surgical_history: "", family_history: "" });

  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [safety, setSafety] = useState(null);
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [savedRx, setSavedRx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audioId, setAudioId] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [vitals, setVitals] = useState({ height: "", weight: "", bp: "", pulse: "", rr: "", spo2: "", temp: "" });
  const [physicalExam, setPhysicalExam] = useState("");
  const [referral, setReferral] = useState({ specialist: "", reason: "" });
  const [followUpChannel, setFollowUpChannel] = useState("sms");
  const [editingId, setEditingId] = useState(null);
  const [chiefAll, setChiefAll] = useState([]);
  const [chiefSel, setChiefSel] = useState([]);
  const [symAll, setSymAll] = useState([]);
  const [symSel, setSymSel] = useState([]);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/meta").then((r) => setSpecialties(r.data.specialties)).catch(() => {});
  }, []);

  useEffect(() => {
    if (presetPatientId) {
      api.get(`/patients/${presetPatientId}`).then((r) => { setPatient(r.data.patient); setStep("record"); }).catch(() => {});
      clearPreset?.();
    }
  }, [presetPatientId]);

  const searchPatients = async (q) => {
    setSearchQ(q);
    if (q.length < 2) { setSearchRes([]); return; }
    const r = await api.get("/patients", { params: { q } });
    setSearchRes(r.data);
  };

  const createPatient = async () => {
    if (!form.name || !form.age) { toast.error("Name and age are required"); return; }
    const payload = { ...form, age: parseInt(form.age), allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean) };
    const r = await api.post("/patients", payload);
    setPatient(r.data);
    toast.success(`Patient registered · ${r.data.patient_id}`);
    setStep("record");
  };

  // Recording
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        uploadAudio(blob, "consult.webm");
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch {
      toast.error("Microphone unavailable. Upload an audio file or type the transcript instead.");
    }
  };

  const stopRec = () => {
    mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  // Transcribe WAV chunks in parallel (cap concurrency) and join in order.
  const transcribeChunks = async (chunks) => {
    const results = new Array(chunks.length);
    const CONCURRENCY = 14;
    let cursor = 0;
    const sendChunk = async (idx) => {
      const fd = new FormData();
      fd.append("file", chunks[idx], `chunk_${idx}.wav`);
      try {
        const r = await api.post("/consultations/transcribe-chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return (r.data.text || "").trim();
      } catch {
        // one retry so a single flaky chunk doesn't force a full-file re-transcription
        await new Promise((res) => setTimeout(res, 500));
        const r = await api.post("/consultations/transcribe-chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return (r.data.text || "").trim();
      }
    };
    const worker = async () => {
      while (cursor < chunks.length) {
        const idx = cursor++;
        results[idx] = await sendChunk(idx);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker));
    return results.filter(Boolean).join(" ").trim();
  };

  const uploadAudio = async (blob, filename) => {
    setTranscribing(true);
    try {
      let text = "";
      let aid = null;
      let chunked = false;
      // Fast path: split into 16kHz mono WAV chunks and transcribe them in parallel.
      try {
        const { chunks, duration } = await toWavChunks(blob, 15);
        if (chunks.length > 1 && duration > 30) {
          const saveFd = new FormData();
          saveFd.append("file", blob, filename);
          const savePromise = api
            .post("/consultations/audio", saveFd, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data.audio_id)
            .catch(() => null);
          text = await transcribeChunks(chunks);
          aid = await savePromise;
          chunked = true;
        }
      } catch {
        // decode/resample unsupported (e.g. some browsers) — fall back to single-file below
      }
      if (!chunked) {
        const fd = new FormData();
        fd.append("file", blob, filename);
        const r = await api.post("/consultations/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
        text = r.data.text;
        aid = r.data.audio_id;
      }
      setTranscript((prev) => (prev ? prev + " " : "") + text);
      if (aid) setAudioId(aid);
      toast.success("Transcribed · recording saved");
    } catch (e) {
      toast.error("Transcription failed: " + (e.response?.data?.detail || e.message));
    } finally { setTranscribing(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadAudio(f, f.name);
  };

  const runAnalysis = async () => {
    if (!transcript.trim()) { toast.error("Add a transcript first"); return; }
    setAnalyzing(true);
    setStep("analyze");
    try {
      const r = await api.post("/consultations/analyze", { patient_id: patient.patient_id, transcript, audio_id: audioId });
      const a = r.data.analysis;
      setAnalysis(a);
      setChiefAll(a.chief_complaints || []); setChiefSel(a.chief_complaints || []);
      setSymAll(a.key_symptoms || []); setSymSel(a.key_symptoms || []);
      setSafety(r.data.safety);
      setStep("review");
      if (r.data.safety?.has_warnings) toast.warning("Drug safety warnings detected — please review");
      else toast.success("AI analysis ready");
    } catch (e) {
      toast.error("Analysis failed: " + (e.response?.data?.detail || e.message));
      setStep("record");
    } finally { setAnalyzing(false); }
  };

  // editing helpers
  const upd = (key, val) => setAnalysis((a) => ({ ...a, [key]: val }));
  const editList = (key, i, val) => setAnalysis((a) => { const l = [...(a[key] || [])]; l[i] = val; return { ...a, [key]: l }; });
  const removeList = (key, i) => setAnalysis((a) => ({ ...a, [key]: a[key].filter((_, idx) => idx !== i) }));
  const addList = (key, val) => setAnalysis((a) => ({ ...a, [key]: [...(a[key] || []), val] }));

  const editMed = (i, field, val) => setAnalysis((a) => { const m = [...a.medications]; m[i] = { ...m[i], [field]: val }; return { ...a, medications: m }; });
  const recheckSafety = async (meds) => {
    try {
      const r = await api.post("/drug-safety/check", { patient_id: patient.patient_id, medications: meds.map((m) => m.name) });
      setSafety(r.data);
    } catch {}
  };

  const toggleChief = (x) => setChiefSel((s) => (s.includes(x) ? s.filter((y) => y !== x) : [...s, x]));
  const addChief = (x) => { setChiefAll((a) => (a.includes(x) ? a : [...a, x])); setChiefSel((s) => (s.includes(x) ? s : [...s, x])); };
  const toggleSym = (x) => setSymSel((s) => (s.includes(x) ? s.filter((y) => y !== x) : [...s, x]));
  const addSym = (x) => { setSymAll((a) => (a.includes(x) ? a : [...a, x])); setSymSel((s) => (s.includes(x) ? s : [...s, x])); };

  const loadForEdit = (rx) => {
    setAnalysis((prev) => ({
      english_transcript: prev?.english_transcript || "",
      transcript: prev?.transcript || prev?.english_transcript || "",
      differential_diagnosis: rx.diagnosis || [],
      diagnostic_tests: rx.tests || [],
      counseling: rx.counseling || [],
      medications: rx.medications || [],
      significant_history: rx.significant_history || "",
      is_emergency: !!rx.is_emergency,
      emergency_referral: rx.emergency_referral || "",
    }));
    setChiefAll(rx.chief_complaints || []); setChiefSel(rx.chief_complaints || []);
    setSymAll(rx.key_symptoms || []); setSymSel(rx.key_symptoms || []);
    setVitals({ height: "", weight: "", bp: "", pulse: "", rr: "", spo2: "", temp: "", ...(rx.vitals || {}) });
    setPhysicalExam(rx.physical_examination || "");
    setReferral({ specialist: rx.referral_specialist || "", reason: rx.referral_reason || "" });
    setNotes(rx.additional_notes || "");
    setFollowUp(rx.follow_up_date || "");
    setFollowUpChannel(rx.follow_up_channel || "sms");
    setEditingId(rx.id);
    setSavedRx(null);
    setStep("review");
  };

  const resetAll = () => {
    setSavedRx(null); setAnalysis(null); setTranscript(""); setPatient(null); setStep("intake");
    setEditingId(null); setChiefAll([]); setChiefSel([]); setSymAll([]); setSymSel([]);
    setVitals({ height: "", weight: "", bp: "", pulse: "", rr: "", spo2: "", temp: "" });
    setPhysicalExam(""); setReferral({ specialist: "", reason: "" }); setNotes(""); setFollowUp(""); setAudioId(null);
  };

  const lockPrescription = async () => {
    if (!savedRx) return;
    try {
      const r = await api.post(`/prescriptions/${savedRx.id}/lock`);
      setSavedRx(r.data);
      toast.success("Counselling done · prescription locked");
    } catch (e) { toast.error("Could not lock: " + (e.response?.data?.detail || e.message)); }
  };

  const savePrescription = async () => {
    setSaving(true);
    try {
      const payload = {
        patient_id: patient.patient_id,
        consultation_id: null,
        chief_complaints: chiefSel,
        key_symptoms: symSel,
        diagnosis: analysis.differential_diagnosis || [],
        tests: analysis.diagnostic_tests || [],
        counseling: analysis.counseling || [],
        medications: analysis.medications || [],
        significant_history: analysis.significant_history || "",
        additional_notes: notes,
        follow_up_date: followUp || null,
        follow_up_channel: followUpChannel,
        vitals,
        physical_examination: physicalExam,
        referral_specialist: referral.specialist,
        referral_reason: referral.reason,
        is_emergency: !!analysis.is_emergency,
        emergency_referral: analysis.emergency_referral || "",
      };
      const r = editingId
        ? await api.put(`/prescriptions/${editingId}`, payload)
        : await api.post("/prescriptions", payload);
      setSavedRx(r.data);
      setEditingId(r.data.id);
      toast.success(editingId ? "Prescription updated" : "Prescription saved · OPD case paper generated");
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  // ---------- Render ----------
  if (savedRx) {
    const locked = savedRx.locked;
    return (
      <div className="space-y-4">
        <div className="no-print flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-2xl font-bold text-slate-900">OPD Case Paper</h2>
            {locked ? (
              <span data-testid="rx-locked-badge" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200"><Lock className="w-3 h-3" /> Locked</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><Pencil className="w-3 h-3" /> Editable</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {!locked && (
              <button data-testid="btn-edit-prescription" onClick={() => loadForEdit(savedRx)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#1E5AA8] text-[#1E5AA8] text-sm font-semibold hover:bg-[#EBF3FB]"><Pencil className="w-4 h-4" /> Edit Prescription</button>
            )}
            {!locked && (
              <button data-testid="btn-done-counselling" onClick={lockPrescription} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><Check className="w-4 h-4" /> Done Counselling</button>
            )}
            <ShareMenu rxId={savedRx.id} patient={patient} doctor={user} />
            <ExportBtn testId="btn-download-pdf" icon={FileDown} label="PDF" onClick={() => downloadFile(`/prescriptions/${savedRx.id}/export?format=pdf`, "post", {}, "opd.pdf")} />
            <ExportBtn testId="btn-download-word" icon={FileText} label="Word" onClick={() => downloadFile(`/prescriptions/${savedRx.id}/export?format=docx`, "post", {}, "opd.docx")} />
            <ExportBtn testId="btn-print-opd" icon={Printer} label="Print" onClick={() => window.print()} />
            <button data-testid="btn-new-consultation" onClick={resetAll}
              className="px-4 py-2.5 rounded-xl bg-[#1E5AA8] text-white text-sm font-semibold hover:bg-[#123C73]">New Consultation</button>
          </div>
        </div>
        {locked && <p className="no-print text-xs text-slate-400">Counselling is marked done — this prescription is now locked and can no longer be edited.</p>}
        <OpdPreview rx={savedRx} patient={patient} doctor={user} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="consultation-tab">
      <StepBar step={step} />

      {step === "intake" && (
        <div className="grid lg:grid-cols-2 gap-5">
          {/* New patient */}
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4"><UserPlus className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">New Patient Intake</h3></div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full Name *" span2><input data-testid="input-patient-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
              <Field label="Age *"><input data-testid="input-patient-age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inp} /></Field>
              <Field label="Gender">
                <select data-testid="select-patient-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inp}>
                  {GENDERS.map((g) => <option key={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} placeholder="+91 98765 43210" /></Field>
              <Field label="WhatsApp (if different)"><input data-testid="input-patient-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={inp} placeholder="With country code" /></Field>
              <Field label="Email" span2><input data-testid="input-patient-email" type="email" placeholder="For sharing prescription" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
              <Field label="Known Drug Allergies (comma separated)" span2><input data-testid="input-patient-allergies" placeholder="Penicillin, Sulfa, NSAIDs" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className={inp} /></Field>
              <Field label="Current / Past Drug History" span2><input value={form.drug_history} onChange={(e) => setForm({ ...form, drug_history: e.target.value })} className={inp} /></Field>
              <Field label="Medical History"><input value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} className={inp} /></Field>
              <Field label="Surgical History"><input value={form.surgical_history} onChange={(e) => setForm({ ...form, surgical_history: e.target.value })} className={inp} /></Field>
              <Field label="Family History" span2><input value={form.family_history} onChange={(e) => setForm({ ...form, family_history: e.target.value })} className={inp} /></Field>
            </div>
            <button data-testid="btn-create-patient" onClick={createPatient} className="mt-4 w-full py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] transition-colors">Register & Continue</button>
          </div>

          {/* Existing patient */}
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4"><Search className="w-5 h-5 text-[#F59E0B]" /><h3 className="font-semibold text-slate-800">Returning Patient</h3></div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="input-search-existing-patient" value={searchQ} onChange={(e) => searchPatients(e.target.value)} placeholder="Search by ID, name or phone…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
            </div>
            <div className="mt-3 space-y-2 max-h-[420px] overflow-auto">
              {searchRes.map((p) => (
                <button key={p.patient_id} onClick={() => { setPatient(p); setStep("record"); }} className="w-full text-left p-3 rounded-xl border border-slate-100 hover:bg-[#EBF3FB] transition-colors">
                  <div className="flex justify-between"><span className="font-medium text-slate-800">{p.name}</span><span className="text-xs font-mono-id text-[#1E5AA8]">{p.patient_id}</span></div>
                  <span className="text-xs text-slate-400">{p.age}/{p.gender} · {p.phone || "no phone"}</span>
                </button>
              ))}
              {searchQ.length >= 2 && searchRes.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No matches</p>}
            </div>
          </div>
        </div>
      )}

      {step !== "intake" && patient && (
        <div className="rounded-2xl bg-[#EBF3FB] border border-blue-100 p-4 flex flex-wrap items-center gap-x-6 gap-y-1">
          <span data-testid="badge-unique-id" className="font-mono-id font-bold text-[#123C73]">{patient.patient_id}</span>
          <span className="text-sm text-slate-700 font-medium">{patient.name}</span>
          <span className="text-sm text-slate-500">{patient.age} / {patient.gender}</span>
          {patient.allergies?.length > 0 && <span className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full">Allergies: {patient.allergies.join(", ")}</span>}
        </div>
      )}

      {(step === "record" || step === "analyze") && (
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
          <div className="flex flex-col items-center py-6">
            {recording ? (
              <div className="flex items-center gap-3 text-[#DC2626] mb-4">
                {[...Array(7)].map((_, i) => <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.1}s` }} />)}
              </div>
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#EBF3FB] flex items-center justify-center mb-4"><Stethoscope className="w-8 h-8 text-[#1E5AA8]" /></div>
            )}
            <p className="text-sm text-slate-500 mb-1">{recording ? "Listening — multilingual detected → English" : transcript ? "Transcript ready. Review & analyze." : "Record the consultation conversation"}</p>
            {recording && <p className="font-mono-id text-2xl text-[#DC2626] mb-4">{mm}:{ss}</p>}
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {!recording ? (
                <button data-testid="btn-start-record" onClick={startRec} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] shadow-md"><Mic className="w-5 h-5" /> Record</button>
              ) : (
                <button data-testid="btn-stop-record" onClick={stopRec} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#DC2626] text-white font-semibold shadow-lg ring-4 ring-red-100 animate-pulse"><Square className="w-5 h-5" /> Stop</button>
              )}
              <label className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 cursor-pointer">
                <Upload className="w-5 h-5" /> Upload audio
                <input data-testid="input-upload-audio" type="file" accept="audio/*" className="hidden" onChange={onFile} />
              </label>
              <button data-testid="btn-load-sample" onClick={() => setTranscript(SAMPLE)} className="px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100">Load sample</button>
            </div>
            {transcribing && <p className="mt-4 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Transcribing audio…</p>}
          </div>

          <div className="mt-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Consultation Transcript (editable)</label>
            <textarea data-testid="textarea-transcript" value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={6} placeholder="Transcript will appear here — you can also type or paste it directly." className="mt-1.5 w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
          </div>

          <button data-testid="btn-analyze-ai" onClick={runAnalysis} disabled={analyzing}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-[#1E5AA8] to-[#123C73] text-white font-semibold hover:opacity-95 disabled:opacity-60">
            {analyzing ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyzing…</> : <><Sparkles className="w-5 h-5" /> Analyze Conversation</>}
          </button>
          {analyzing && <div className="mt-3 h-1.5 rounded-full aura-shimmer" />}
        </div>
      )}

      {step === "review" && analysis && (
        <div className="space-y-5">
          {analysis.is_emergency && (
            <div data-testid="emergency-banner" className="rounded-2xl bg-red-600 text-white p-4 shadow-lg">
              <div className="flex items-center gap-2 font-semibold mb-2"><AlertTriangle className="w-5 h-5" /> Emergency — Immediate External Referral Advised</div>
              <input value={analysis.emergency_referral || ""} onChange={(e) => upd("emergency_referral", e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm text-slate-800" placeholder="e.g. Refer immediately to nearest emergency department…" />
            </div>
          )}
          {safety?.has_warnings && (
            <div data-testid="safety-warning-banner" className="rounded-2xl bg-[#FEF2F2] border border-red-200 p-4">
              <div className="flex items-center gap-2 text-red-700 font-semibold mb-2"><ShieldAlert className="w-5 h-5" /> Drug Safety Warnings</div>
              <ul className="space-y-1">
                {safety.warnings.map((w, i) => (
                  <li key={i} className="text-sm text-red-700 flex items-start gap-2"><span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${w.severity === "high" ? "bg-red-600" : "bg-amber-500"}`} />{w.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            <TranscriptCard analysis={analysis} />
            <EditCard testId="card-ai-symptoms" title="Chief Complaints & Key Symptoms">
              <p className="text-xs text-slate-400 mb-2">Tap a suggestion to select/deselect it, or type to add your own.</p>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Chief Complaints</label>
              <SelectableChips all={chiefAll} selected={chiefSel} onToggle={toggleChief} onAdd={addChief} testId="chips-chief" addLabel="Add complaint" />
              <label className="text-xs font-semibold text-slate-500 mb-1 block mt-4">Key Symptoms</label>
              <SelectableChips all={symAll} selected={symSel} onToggle={toggleSym} onAdd={addSym} testId="chips-symptoms" addLabel="Add symptom" />
            </EditCard>
          </div>

          <EditCard testId="card-vitals" title="Vital Signs (recorded at clinic)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["bp", "BP (mmHg)", "120/80"], ["pulse", "Pulse (/min)", "78"], ["rr", "RR (/min)", "16"], ["spo2", "SpO₂ (%)", "98"], ["temp", "Temp (°F)", "98.6"], ["height", "Height (cm)", "170"], ["weight", "Weight (kg)", "65"]].map(([k, label, ph]) => (
                <div key={k}><label className="text-xs text-slate-500">{label}</label><input data-testid={`vital-${k}`} value={vitals[k]} onChange={(e) => setVitals({ ...vitals, [k]: e.target.value })} placeholder={ph} className={inpSm + " w-full"} /></div>
              ))}
            </div>
            <label className="text-xs text-slate-500 mt-3 block">Physical Examination</label>
            <textarea data-testid="textarea-physical-exam" value={physicalExam} onChange={(e) => setPhysicalExam(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" placeholder="General & systemic examination findings…" />
          </EditCard>

          <div className="grid lg:grid-cols-2 gap-5">
            <EditCard testId="card-ai-diagnosis" title="Differential Diagnosis">
              <p className="text-xs text-slate-400 mb-2">Tap the flag to mark a red-flag / emergency diagnosis (shown in red). Only diagnoses set to <b>High</b> likelihood are printed on the prescription.</p>
              {(analysis.differential_diagnosis || []).map((d, i) => (
                <div key={i} className={`flex items-center gap-2 mb-2 rounded-lg ${d.red_flag ? "bg-red-50 border border-red-200 px-2 py-1.5" : ""}`}>
                  <button data-testid={`btn-redflag-${i}`} onClick={() => editList("differential_diagnosis", i, { ...d, red_flag: !d.red_flag })} title="Toggle red flag / emergency" className={`shrink-0 p-1 rounded ${d.red_flag ? "text-red-600" : "text-slate-300 hover:text-red-400"}`}><AlertTriangle className="w-4 h-4" /></button>
                  <input value={d.condition || ""} onChange={(e) => editList("differential_diagnosis", i, { ...d, condition: e.target.value })} className={`${inpSm} flex-1 ${d.red_flag ? "text-red-700 font-semibold" : ""}`} />
                  <select data-testid={`select-dx-likelihood-${i}`} value={d.likelihood || "Moderate"} onChange={(e) => editList("differential_diagnosis", i, { ...d, likelihood: e.target.value })} className="text-xs rounded-lg border border-slate-200 px-2 py-2">
                    {["High", "Moderate", "Low"].map((x) => <option key={x}>{x}</option>)}
                  </select>
                  <button onClick={() => removeList("differential_diagnosis", i)} className="text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="mt-2">
                <AutocompleteAdder endpoint="/diagnoses" placeholder="Add diagnosis — your saved ones are suggested…" testId="input-diagnosis-autocomplete" allowCustom onAdd={(item) => addList("differential_diagnosis", { condition: item.name, likelihood: "High", red_flag: false })} />
              </div>
            </EditCard>

            <EditCard testId="card-ai-tests" title="Diagnostic Tests">
              {(analysis.diagnostic_tests || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input value={t.name || ""} onChange={(e) => editList("diagnostic_tests", i, { ...t, name: e.target.value })} className={inpSm + " flex-1"} />
                  <button onClick={() => removeList("diagnostic_tests", i)} className="text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="mt-2">
                <AutocompleteAdder endpoint="/labtests" placeholder="Type to search lab tests from master…" testId="input-labtest-autocomplete" allowCustom onAdd={(item) => addList("diagnostic_tests", { name: item.name })} />
              </div>
            </EditCard>
          </div>

          <EditCard testId="card-ai-treatment" title="Treatment — Generic name · Dose · Frequency · Duration">
            <div className="space-y-3">
              {(analysis.medications || []).map((m, i) => (
                <div key={i} className="rounded-xl border border-slate-100 p-3 bg-slate-50/50">
                  <div className="grid sm:grid-cols-12 gap-2">
                    <div className="sm:col-span-5"><MedicineAutocomplete value={m.name || ""} onChange={(v) => editMed(i, "name", v)} onSelect={(item) => { editMed(i, "name", item.name); recheckSafety(analysis.medications.map((x, idx) => idx === i ? { ...x, name: item.name } : x)); }} placeholder="Generic medicine…" testId={`input-medicine-autocomplete-${i}`} /></div>
                    <input value={m.dose || ""} onChange={(e) => editMed(i, "dose", e.target.value)} placeholder="Dose" className={`sm:col-span-2 ${inpSm}`} />
                    <input value={m.frequency || ""} onChange={(e) => editMed(i, "frequency", e.target.value)} placeholder="1-0-1" className={`sm:col-span-2 ${inpSm}`} />
                    <input value={m.duration || ""} onChange={(e) => editMed(i, "duration", e.target.value)} placeholder="5 days" className={`sm:col-span-2 ${inpSm}`} />
                    <button onClick={() => { removeList("medications", i); recheckSafety(analysis.medications.filter((_, idx) => idx !== i)); }} className="sm:col-span-1 flex items-center justify-center text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input value={m.instructions || ""} onChange={(e) => editMed(i, "instructions", e.target.value)} placeholder="Instructions (e.g. after food)" className={`mt-2 ${inpSm} w-full`} />
                </div>
              ))}
              <button data-testid="btn-add-medicine-row" onClick={() => addList("medications", { name: "", dose: "", frequency: "", duration: "", instructions: "" })} className="inline-flex items-center gap-2 text-sm text-[#1E5AA8] font-medium hover:gap-3 transition-all"><Plus className="w-4 h-4" /> Add medicine</button>
            </div>
          </EditCard>

          <EditCard testId="card-ai-counseling" title="Non-Pharmacological Counseling & Advice">
            <TagEditor items={analysis.counseling || []} onEdit={(i, v) => editList("counseling", i, v)} onRemove={(i) => removeList("counseling", i)} onAdd={(v) => addList("counseling", v)} placeholder="Add advice" vertical />
          </EditCard>

          <EditCard testId="card-referral" title="Specialist Referral (optional)">
            <div className="grid sm:grid-cols-2 gap-3">
              <select data-testid="select-referral-specialist" value={referral.specialist} onChange={(e) => setReferral({ ...referral, specialist: e.target.value })} className={inpSm + " w-full"}>
                <option value="">No referral</option>
                {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input data-testid="input-referral-reason" value={referral.reason} onChange={(e) => setReferral({ ...referral, reason: e.target.value })} placeholder="Reason for referral" className={inpSm + " w-full"} />
            </div>
          </EditCard>

          <div className="grid lg:grid-cols-2 gap-5">
            <EditCard title="Additional Notes">
              <textarea data-testid="textarea-additional-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" placeholder="Any additional observations…" />
            </EditCard>
            <EditCard title="Follow-up Date & Reminder">
              <input data-testid="input-followup-date" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className={inp} />
              <label className="text-xs text-slate-500 mt-3 block">Reminder channel</label>
              <select data-testid="select-followup-channel" value={followUpChannel} onChange={(e) => setFollowUpChannel(e.target.value)} className={inp}>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
              <p className="mt-2 text-[11px] text-slate-400">An automatic reminder is sent to the patient's phone on the follow-up date.</p>
            </EditCard>
          </div>

          <div className="flex flex-wrap gap-3 justify-end">
            <button onClick={() => setStep("record")} className="px-5 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50">Back</button>
            <button data-testid="btn-save-prescription" onClick={savePrescription} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] disabled:opacity-60">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save & Generate OPD Paper
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30 focus:border-[#1E5AA8]";
const inpSm = "px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30";

function Field({ label, span2, children }) {
  return <div className={span2 ? "col-span-2" : ""}><label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>{children}</div>;
}
function ExportBtn({ testId, icon: Icon, label, onClick }) {
  return <button data-testid={testId} onClick={onClick} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-slate-300"><Icon className="w-4 h-4" /> {label}</button>;
}
function EditCard({ testId, title, children }) {
  return <div data-testid={testId} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5"><h3 className="font-semibold text-slate-800 mb-3">{title}</h3>{children}</div>;
}
function TranscriptCard({ analysis }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-2"><h3 className="font-semibold text-slate-800">English Clinical Transcript</h3></div>
      <p className="text-sm text-slate-600 leading-relaxed max-h-56 overflow-auto whitespace-pre-wrap">{analysis.transcript || analysis.english_transcript}</p>
    </div>
  );
}
function TagEditor({ items, onEdit, onRemove, onAdd, placeholder, vertical }) {
  return (
    <div className={vertical ? "space-y-2" : ""}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <input value={it} onChange={(e) => onEdit(i, e.target.value)} className={inpSm + " flex-1"} />
          <button onClick={() => onRemove(i)} className="text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <AddInline onAdd={onAdd} label={placeholder} />
    </div>
  );
}
function AddInline({ onAdd, label }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-2">
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) { onAdd(v.trim()); setV(""); } }} placeholder={label} className={inpSm + " flex-1"} />
      <button onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(""); } }} className="p-2 rounded-lg bg-[#EBF3FB] text-[#1E5AA8] hover:bg-blue-100"><Plus className="w-4 h-4" /></button>
    </div>
  );
}
function SelectableChips({ all, selected, onToggle, onAdd, testId, addLabel }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onAdd(v.trim()); setV(""); } };
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {all.length === 0 && <span className="text-xs text-slate-300">No suggestions — add your own below.</span>}
        {all.map((x, i) => {
          const on = selected.includes(x);
          return (
            <button key={i} type="button" data-testid={`${testId}-item-${i}`} onClick={() => onToggle(x)}
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#1E5AA8] text-white border-[#1E5AA8]" : "bg-white text-slate-500 border-slate-200 hover:border-[#1E5AA8] hover:text-[#1E5AA8]"}`}>
              {on && <Check className="w-3 h-3" />} {x}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input data-testid={`${testId}-input`} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder={addLabel} className={inpSm + " flex-1"} />
        <button data-testid={`${testId}-add`} onClick={add} className="p-2 rounded-lg bg-[#EBF3FB] text-[#1E5AA8] hover:bg-blue-100"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
function AutocompleteAdder({ endpoint, placeholder, testId, onAdd, allowCustom }) {
  const [q, setQ] = useState("");
  return (
    <div>
      <MedicineAutocomplete endpoint={endpoint} value={q} onChange={setQ} onSelect={(item) => { onAdd(item); setQ(""); }} placeholder={placeholder} testId={testId} />
      {allowCustom && q.trim().length >= 2 && (
        <button type="button" data-testid={`${testId}-add-custom`} onClick={() => { onAdd({ name: q.trim(), custom: true }); setQ(""); }} className="mt-1.5 text-xs text-[#1E5AA8] font-medium inline-flex items-center gap-1 hover:underline">
          <Plus className="w-3 h-3" /> Add "{q.trim()}" (custom, not in master)
        </button>
      )}
    </div>
  );
}
function StepBar({ step }) {
  const steps = [["intake", "Patient"], ["record", "Record"], ["review", "Review Rx"]];
  const idx = { intake: 0, record: 1, analyze: 1, review: 2 }[step];
  return (
    <div className="flex items-center gap-2">
      {steps.map(([id, label], i) => (
        <div key={id} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${i <= idx ? "bg-[#1E5AA8] text-white" : "bg-white border border-slate-200 text-slate-400"}`}>
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{i + 1}</span>{label}
          </div>
          {i < steps.length - 1 && <div className={`w-6 h-0.5 ${i < idx ? "bg-[#1E5AA8]" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
}
